/**
 * Saved additions profile commands (PLAN-ADDITIONS-PROFILES).
 *
 * The same four verbs as the mash and fermentation libraries, written out
 * longhand for the reason given at the top of `schedule-profile.ts`.
 *
 * Nothing ships built in, so `builtin` is false on everything `create` makes.
 * `update` and `delete` still refuse a built-in, because `restore` takes a whole
 * document and could in principle put one there.
 *
 * ## Applying is not here
 *
 * There is no `recipe.applyMiscProfile`. Applying is `recipe.addMisc` once per
 * row, in one `dispatchAll`, which is what the misc table does. The scaling and
 * duplicate skipping live in the app (`lib/misc-profile.ts`) because they
 * decide which commands to send, and the log only needs the result.
 */

import type { MiscProfileDoc } from '../../document/misc-profile';
import { DEFAULT_BATCH_VOLUME_L } from '../../document/recipe';
import type { WorkspaceState } from '../../document/workspace';
import { defineCommand, inverseCommand } from '../definition';
import { commandError } from '../envelope';
import { quoted } from '../format';
import { mergeDefined, priorValues } from '../helpers';
import * as p from '../payloads';

/** Put an additions profile back into the workspace. */
function putMisc(state: WorkspaceState, profile: MiscProfileDoc): WorkspaceState {
  return { ...state, miscProfiles: { ...state.miscProfiles, [profile.id]: profile } };
}

/** The refusal `update` and `delete` give for a built-in. */
function refuseBuiltin(name: string, commandId: string, commandType: string) {
  return commandError(
    'invalid-state',
    `${quoted(name)} is a built-in additions profile. Duplicate it to make your own version.`,
    { commandId, commandType },
  );
}

/** The "no such profile" refusal. */
function notFound(profileId: string, commandId: string, commandType: string) {
  return commandError('not-found', `No additions profile with id "${profileId}".`, {
    commandId,
    commandType,
  });
}

/** Save an additions profile, blank or duplicated from another. */
export const miscProfileCreate = defineCommand({
  type: 'miscProfile.create',
  payload: p.miscProfileCreatePayloadSchema,
  apply: (state, command) => {
    const { profileId, name, from } = command.payload;
    if (state.miscProfiles[profileId] !== undefined) {
      return {
        ok: false,
        error: commandError(
          'conflict',
          `An additions profile with id "${profileId}" already exists.`,
          {
            commandId: command.commandId,
            commandType: command.type,
          },
        ),
      };
    }
    // A blank profile is written for a new recipe's batch size until the
    // update that follows a save from a recipe sets the real one.
    const profile: MiscProfileDoc = {
      notes: from?.notes ?? '',
      batchVolumeL: from?.batchVolumeL ?? DEFAULT_BATCH_VOLUME_L,
      styleIds: from?.styleIds ?? [],
      miscs: from?.miscs ?? [],
      id: profileId,
      name,
      builtin: false,
      createdAt: command.timestamp,
      updatedAt: command.timestamp,
    };
    return { ok: true, state: putMisc(state, profile) };
  },
  invert: (_state, command) =>
    inverseCommand(
      command,
      'miscProfile.delete',
      { profileId: command.payload.profileId },
      command.timestamp,
    ),
  summarize: (_state, command) => `Save additions profile ${quoted(command.payload.name)}`,
});

/** Change a saved additions profile. Built-ins refuse. */
export const miscProfileUpdate = defineCommand({
  type: 'miscProfile.update',
  payload: p.miscProfileUpdatePayloadSchema,
  apply: (state, command) => {
    const profile = state.miscProfiles[command.payload.profileId];
    if (profile === undefined) {
      return {
        ok: false,
        error: notFound(command.payload.profileId, command.commandId, command.type),
      };
    }
    if (profile.builtin) {
      return { ok: false, error: refuseBuiltin(profile.name, command.commandId, command.type) };
    }
    return {
      ok: true,
      state: putMisc(state, {
        ...mergeDefined(profile, command.payload.changes),
        updatedAt: command.timestamp,
      }),
    };
  },
  invert: (state, command) => {
    const profile = state.miscProfiles[command.payload.profileId];
    return profile === undefined
      ? null
      : inverseCommand(
          command,
          'miscProfile.update',
          { profileId: profile.id, changes: priorValues(profile, command.payload.changes) },
          profile.updatedAt,
        );
  },
  summarize: (state, command) => {
    const profile = state.miscProfiles[command.payload.profileId];
    const keys = Object.keys(command.payload.changes).sort().join(', ');
    return `Update additions profile ${quoted(profile?.name ?? command.payload.profileId)}${
      keys === '' ? '' : ` (${keys})`
    }`;
  },
});

/** Delete a saved additions profile. Built-ins refuse. */
export const miscProfileDelete = defineCommand({
  type: 'miscProfile.delete',
  payload: p.miscProfileDeletePayloadSchema,
  apply: (state, command) => {
    const profile = state.miscProfiles[command.payload.profileId];
    if (profile === undefined) {
      return {
        ok: false,
        error: notFound(command.payload.profileId, command.commandId, command.type),
      };
    }
    if (profile.builtin) {
      return { ok: false, error: refuseBuiltin(profile.name, command.commandId, command.type) };
    }
    const next = { ...state.miscProfiles };
    delete next[command.payload.profileId];
    return { ok: true, state: { ...state, miscProfiles: next } };
  },
  // Inverts to a restore rather than a create, so undo is an identity: the
  // timestamps come back too. Same reason `equipment.delete` does.
  invert: (state, command) => {
    const profile = state.miscProfiles[command.payload.profileId];
    return profile === undefined
      ? null
      : inverseCommand(command, 'miscProfile.restore', { profile }, profile.updatedAt);
  },
  summarize: (state, command) => {
    const profile = state.miscProfiles[command.payload.profileId];
    return `Delete additions profile ${quoted(profile?.name ?? command.payload.profileId)}`;
  },
});

/** Insert a saved additions profile verbatim — the inverse of a delete. */
export const miscProfileRestore = defineCommand({
  type: 'miscProfile.restore',
  payload: p.miscProfileRestorePayloadSchema,
  apply: (state, command) => ({ ok: true, state: putMisc(state, command.payload.profile) }),
  invert: (_state, command) =>
    inverseCommand(
      command,
      'miscProfile.delete',
      { profileId: command.payload.profile.id },
      command.timestamp,
    ),
  summarize: (_state, command) =>
    `Restore additions profile ${quoted(command.payload.profile.name)}`,
});
